// Product.jsx - Gold/Silver Product Master
import React, { useEffect, useState, useRef } from "react";
import {
  Plus, Download, Upload, Trash2, Save, Search, RefreshCw,
  X, CheckCircle, Clock, ChevronLeft, ChevronRight,
  Edit, Hash
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";


const API_URL = "https://omapi.omgoldfinance.live/api/products";
const SUPPLIER_API_URL = "https://omapi.omgoldfinance.live/api";
const BILLING_API_URL = "https://omapi.omgoldfinance.live/api/billing";

export default function ItemsPage() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importedItems, setImportedItems] = useState([]);
  const [processingImport, setProcessingImport] = useState(false);
  const [importStats, setImportStats] = useState({ added: 0, updated: 0, skipped: 0 });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);



  // Track processed supply items to prevent duplicates
  const processedItemIds = useRef(new Set());
  const isProcessing = useRef(false);

  // Load products whenever currentPage or search changes (global server-side search)
  useEffect(() => {
    loadProducts(currentPage, search);
  }, [currentPage, search]);

  // Auto-hide message after 3 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Check for pending supply items on component mount and periodically
  useEffect(() => {
    const initialCheck = setTimeout(() => {
      checkAndProcessPendingSupplies();
    }, 1000);

    const interval = setInterval(checkAndProcessPendingSupplies, 30000);

    return () => {
      clearTimeout(initialCheck);
      clearInterval(interval);
    };
  }, []);

  const showMessage = (type, text) => {
    setMessage({ type, text });
  };

  const normalizeProductIds = (products) => {
    const sortedProducts = [...products].sort((a, b) => {
      const getSortValue = (product) => {
        const idValue = Number(product?.id);
        if (Number.isFinite(idValue)) {
          return idValue;
        }

        const parsedId = Number(String(product?.id || '').replace(/\D/g, ''));
        return Number.isFinite(parsedId) ? parsedId : Number.MAX_SAFE_INTEGER;
      };

      const aValue = getSortValue(a);
      const bValue = getSortValue(b);

      if (aValue !== bValue) {
        return aValue - bValue;
      }

      const aDate = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const bDate = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return aDate - bDate;
    });

    return sortedProducts.map((product, index) => ({
      ...product,
      displayId: index + 1,
    }));
  };

  const loadProducts = async (page = 1, searchQuery = search) => {
    setLoading(true);
    try {
      let url = `${API_URL}?page=${page}&per_page=${itemsPerPage}`;
      if (searchQuery && searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      
      const data = await res.json();
      
      let productsArray = [];
      if (data && data.items && Array.isArray(data.items)) {
        productsArray = data.items;
        setTotalItems(data.total || 0);
        setTotalPages(data.pages || 1);
      } else if (Array.isArray(data)) {
        productsArray = data;
        setTotalItems(productsArray.length);
        setTotalPages(Math.ceil(productsArray.length / itemsPerPage));
      } else {
        console.warn('Unexpected API response format:', data);
        productsArray = [];
        setTotalItems(0);
        setTotalPages(1);
      }

      setItems(normalizeProductIds(productsArray));
    } catch (err) {
      console.error("Error fetching products:", err);
      showMessage("error", "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  // ================= AUTO-PROCESS PENDING SUPPLIES =================
  const checkAndProcessPendingSupplies = async () => {
    if (isProcessing.current) return;
    try {
      isProcessing.current = true;
      console.log("Checking for pending supply items...");
      
      // Fetch all suppliers with their items
      const res = await fetch(`${SUPPLIER_API_URL}/suppliers-with-items`, {
        credentials: 'include'
      });
      
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      
      const data = await res.json();
      
      if (data.success && data.suppliers) {
        const pendingItems = [];
        data.suppliers.forEach(supplier => {
          if (supplier.items && supplier.items.length > 0) {
            supplier.items.forEach(item => {
              if (item.status === "Pending" && !processedItemIds.current.has(item.id)) {
                pendingItems.push({ ...item, supplierName: supplier.name, supplierId: supplier.id });
              }
            });
          }
        });
        if (pendingItems.length > 0) {
          await processPendingSupplies(pendingItems);
        }
      }
    } catch (err) {
      console.error("Error checking pending supplies:", err);
    } finally {
      isProcessing.current = false;
    }
  };

  const processPendingSupplies = async (pendingItems) => {
    try {
      let addedCount = 0;
      const successfullyProcessed = [];

      for (const supplyItem of pendingItems) {
        try {
          if (processedItemIds.current.has(supplyItem.id)) continue;

          const newItem = {
            name: supplyItem.name,
            code: supplyItem.code || "",
            goldRate: parseFloat(supplyItem.gold_rate || supplyItem.goldRate || 0),
            silverRate: parseFloat(supplyItem.silver_rate || supplyItem.silverRate || 0),
            grossWeight: parseFloat(supplyItem.gross_weight || supplyItem.grossWeight || 0),
            stoneWaxWeight: parseFloat(supplyItem.stone_wax_weight || supplyItem.stoneWaxWeight || 0),
            purity: parseFloat(supplyItem.purity || 91),
            itemType: supplyItem.item_type || supplyItem.itemType || "gold",
            quantity: parseInt(supplyItem.quantity) || 1,
          };

          const createRes = await fetch(API_URL, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(newItem),
            });

          if (createRes.status >= 200 && createRes.status < 300) {
            addedCount++;
            successfullyProcessed.push(supplyItem.id);
          }

          if (successfullyProcessed.includes(supplyItem.id)) {
            processedItemIds.current.add(supplyItem.id);
            await updateSupplyItemStatus(supplyItem.id, "In Inventory");
          }
        } catch (itemError) {
          console.error(`Error processing supply item ${supplyItem.id}:`, itemError);
        }
      }

      if (addedCount > 0) {
        await loadProducts(currentPage);
        showMessage("success", `Auto-processed ${addedCount} supply item(s) into inventory`);
      }
    } catch (err) {
      console.error("Error processing pending supplies:", err);
    }
  };

  const updateSupplyItemStatus = async (itemId, newStatus) => {
    try {
      await fetch(`${SUPPLIER_API_URL}/items/${itemId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
    } catch (err) {
      console.error(`Error updating status for item ${itemId}:`, err);
    }
  };



  // ================= GOLD CALCULATION =================
  const calculateGoldValues = (item) => {
    const grossWeight = parseFloat(item.grossWeight) || 0;
    const stoneWaxWeight = parseFloat(item.stoneWaxWeight) || 0;
    const purity = parseFloat(item.purity) || 91;
    const goldRate = parseFloat(item.goldRate) || 0;
    const silverRate = parseFloat(item.silverRate) || 0;
    const itemType = item.itemType || 'gold';
    const rate = itemType === 'silver' ? silverRate : goldRate;

    const netWeight = Math.max(0, parseFloat((grossWeight - stoneWaxWeight).toFixed(4)));
    const grossAmount = parseFloat((netWeight * (purity / 100) * rate).toFixed(2));

    return { ...item, netWeight, grossAmount };
  };

  // ================= EDIT ITEM FUNCTIONS =================
  const handleEditClick = (item) => {
    setEditingItem({ ...item });
    setShowEditModal(true);
  };

  const handleEditChange = (field, value) => {
    setEditingItem(prev => {
      const updated = { ...prev, [field]: value };
      // Recalculate on weight/rate/purity change
      if (['grossWeight', 'stoneWaxWeight', 'purity', 'goldRate', 'silverRate', 'itemType'].includes(field)) {
        return calculateGoldValues(updated);
      }
      return updated;
    });
  };

  const handleEditSave = async () => {
    if (!editingItem) return;

    if (!editingItem.name || editingItem.name.trim() === '') {
      showMessage("error", "Item name is required");
      return;
    }

    const gross = parseFloat(editingItem.grossWeight) || 0;
    const stoneWax = parseFloat(editingItem.stoneWaxWeight) || 0;
    if (stoneWax > gross) {
      showMessage("error", "Stone/Wax weight cannot exceed Gross weight");
      return;
    }
    const purity = parseFloat(editingItem.purity) || 91;
    if (purity < 60 || purity > 99.9) {
      showMessage("error", "Purity must be between 60% and 99.9%");
      return;
    }

    setSaving(true);
    try {
      const productData = {
        name: editingItem.name.trim(),
        code: editingItem.code?.trim() || "",
        goldRate: parseFloat(editingItem.goldRate) || 0,
        silverRate: parseFloat(editingItem.silverRate) || 0,
        grossWeight: parseFloat(editingItem.grossWeight) || 0,
        stoneWaxWeight: parseFloat(editingItem.stoneWaxWeight) || 0,
        purity: parseFloat(editingItem.purity) || 91,
        itemType: editingItem.itemType || "gold",
        quantity: parseInt(editingItem.quantity) || 1,
      };

      let response;
      if (editingItem.isNew) {
        response = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(productData),
        });
      } else {
        response = await fetch(`${API_URL}/${editingItem.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(productData),
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.errors ? errorData.errors.join(', ') : `Failed to ${editingItem.isNew ? 'create' : 'update'} product`);
      }

      showMessage("success", `Item ${editingItem.isNew ? 'created' : 'updated'} successfully!`);
      setShowEditModal(false);
      setEditingItem(null);
      await loadProducts(currentPage);
    } catch (err) {
      showMessage("error", `Failed to save item: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ================= ADD NEW ITEM =================
  const handleAddNewItem = () => {
    const newItem = {
      id: `new-${Date.now()}`,
      name: "",
      code: "",
      goldRate: 0,
      silverRate: 0,
      grossWeight: 0,
      stoneWaxWeight: 0,
      netWeight: 0,
      purity: 91,
      grossAmount: 0,
      itemType: "gold",
      quantity: 1,
      isNew: true,
    };
    setEditingItem(newItem);
    setShowEditModal(true);
  };

  const handleRefresh = async () => {
    await loadProducts(currentPage);
    showMessage("success", "Data refreshed!");
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      if (!String(id).startsWith("new-")) {
        const res = await fetch(`${API_URL}/${id}`, { method: "DELETE" });
        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.error || "Failed to delete product");
        }
      }
      showMessage("success", "Item deleted successfully");
      await loadProducts(currentPage);
    } catch (err) {
      showMessage("error", `Failed to delete item: ${err.message}`);
    }
  };

  // ================= EXPORT TO EXCEL =================
  const handleExport = async () => {
    try {
      const res = await fetch(`${API_URL}?page=1&per_page=1000`);
      const data = await res.json();
      let productsArray = data?.items && Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];

      const exportData = productsArray.map(item => ({
        'ID': item.id || '',
        'Item Name': item.name || '',
        'Code': item.code || '',
        'Item Type': item.itemType || 'gold',
        'Gold Rate (₹/g)': item.goldRate || 0,
        'Silver Rate (₹/g)': item.silverRate || 0,
        'Gross Weight (g)': item.grossWeight || 0,
        'Stone/Wax Weight (g)': item.stoneWaxWeight || 0,
        'Net Weight (g)': item.netWeight || 0,
        'Purity (%)': item.purity || 91,
        'Gross Amount (₹)': item.grossAmount || 0,
        'Quantity': item.quantity || 1,
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Gold Products");

      worksheet['!cols'] = [
        { wch: 6 }, { wch: 20 }, { wch: 10 }, { wch: 10 },
        { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 20 },
        { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 10 },
      ];

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const file = new Blob([excelBuffer], { type: "application/octet-stream" });
      const date = new Date().toISOString().split('T')[0];
      saveAs(file, `GoldProducts_${date}.xlsx`);
      showMessage("success", "Export successful!");
    } catch (err) {
      showMessage("error", "Failed to export");
    }
  };

  // ================= IMPORT FROM EXCEL =================
  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          showMessage("error", "No data found in the file");
          return;
        }

        const processedItems = jsonData.map((row, index) => {
          const name = row['Item Name'] || row['Name'] || row['name'] || '';
          const code = row['Code'] || row['code'] || '';
          const itemType = row['Item Type'] || row['itemType'] || 'gold';
          const goldRate = parseFloat(row['Gold Rate (₹/g)'] || row['goldRate'] || row['Gold Rate'] || 0);
          const silverRate = parseFloat(row['Silver Rate (₹/g)'] || row['silverRate'] || 0);
          const grossWeight = parseFloat(row['Gross Weight (g)'] || row['grossWeight'] || 0);
          const stoneWaxWeight = parseFloat(row['Stone/Wax Weight (g)'] || row['stoneWaxWeight'] || 0);
          const purity = parseFloat(row['Purity (%)'] || row['purity'] || 91);
          const quantity = parseInt(row['Quantity'] || row['quantity'] || 1);

          const netWeight = Math.max(0, grossWeight - stoneWaxWeight);
          const rate = itemType === 'silver' ? silverRate : goldRate;
          const grossAmount = netWeight * (purity / 100) * rate;

          return {
            id: `import-${Date.now()}-${index}`,
            name, code, itemType, goldRate, silverRate,
            grossWeight, stoneWaxWeight, netWeight, purity, grossAmount,
            quantity, isNew: true, selected: true,
          };
        }).filter(item => item.name);

        if (processedItems.length === 0) {
          showMessage("error", "No valid items found. Ensure 'Item Name' column exists.");
          return;
        }

        setImportedItems(processedItems);
        setShowImportModal(true);
        setImportStats({ added: 0, updated: 0, skipped: 0 });
        e.target.value = '';
      } catch (err) {
        showMessage("error", "Failed to import file: " + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // ================= PROCESS IMPORTED ITEMS =================
  const processImportedItems = async () => {
    const itemsToProcess = importedItems.filter(item => item.selected);
    if (itemsToProcess.length === 0) {
      showMessage("error", "No items selected for import");
      return;
    }

    setProcessingImport(true);
    let added = 0, skipped = 0;

    try {
      for (const importItem of itemsToProcess) {
        try {
          const newItem = {
            name: importItem.name,
            code: importItem.code || "",
            goldRate: parseFloat(importItem.goldRate) || 0,
            silverRate: parseFloat(importItem.silverRate) || 0,
            grossWeight: parseFloat(importItem.grossWeight) || 0,
            stoneWaxWeight: parseFloat(importItem.stoneWaxWeight) || 0,
            purity: parseFloat(importItem.purity) || 91,
            itemType: importItem.itemType || "gold",
            quantity: parseInt(importItem.quantity) || 1,
          };

          const createRes = await fetch(API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(newItem),
          });

          if (createRes.ok) added++;
          else skipped++;
        } catch (itemError) {
          skipped++;
        }
      }

      setImportStats({ added, updated: 0, skipped });
      await loadProducts(currentPage);
      showMessage("success", `Import completed! ✅ ${added} added, ⏭️ ${skipped} skipped`);
      setTimeout(() => { setShowImportModal(false); setImportedItems([]); }, 3000);
    } catch (err) {
      showMessage("error", "Failed to process import: " + err.message);
    } finally {
      setProcessingImport(false);
    }
  };

  // ================= TOGGLE IMPORT ITEM SELECTION =================
  const toggleImportItem = (index) => {
    setImportedItems(prev => prev.map((item, i) => i === index ? { ...item, selected: !item.selected } : item));
  };

  const toggleAllImportItems = () => {
    const allSelected = importedItems.every(item => item.selected);
    setImportedItems(prev => prev.map(item => ({ ...item, selected: !allSelected })));
  };

  // ================= PAGINATION =================
  const getCurrentPageItems = () => {
    return items;
  };

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      loadProducts(pageNumber, search);
    }
  };

  const goToPreviousPage = () => { if (currentPage > 1) paginate(currentPage - 1); };
  const goToNextPage = () => { if (currentPage < totalPages) paginate(currentPage + 1); };

  const filteredItems = items;

  const currentItems = getCurrentPageItems();

  // ================= MODAL STYLES =================
  const modalStyles = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    content: { backgroundColor: '#1f2937', padding: '28px', borderRadius: '12px', width: '90%', maxWidth: '560px', maxHeight: '90vh', overflow: 'auto', border: '1px solid #374151', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' },
    largeContent: { backgroundColor: '#1f2937', padding: '24px', borderRadius: '12px', width: '95%', maxWidth: '1000px', maxHeight: '85vh', overflow: 'auto', border: '1px solid #374151' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
    modalTitle: { fontSize: '20px', fontWeight: '700', color: '#f9fafb' },
    closeButton: { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '4px', borderRadius: '4px' },
    formGroup: { marginBottom: '14px' },
    formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' },
    label: { display: 'block', marginBottom: '6px', color: '#9ca3af', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' },
    input: { width: '100%', padding: '10px 12px', backgroundColor: '#111827', border: '1px solid #374151', color: '#fff', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', transition: 'border-color 0.2s' },
    readOnlyField: { width: '100%', padding: '10px 12px', backgroundColor: '#0f1724', border: '1px solid #1e3a5f', color: '#60a5fa', borderRadius: '6px', fontSize: '14px', fontWeight: '600', boxSizing: 'border-box' },
    sectionLabel: { fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid #374151' },
    modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #374151' },
    importTable: { width: '100%', borderCollapse: 'collapse', marginTop: '15px' },
    importTh: { backgroundColor: '#374151', padding: '10px', textAlign: 'left', color: '#f3f4f6', fontSize: '12px', position: 'sticky', top: 0 },
    importTd: { padding: '10px', borderBottom: '1px solid #374151', color: '#f9fafb', fontSize: '13px' },
    checkbox: { width: '18px', height: '18px', cursor: 'pointer', accentColor: '#6366f1' },
    statsContainer: { display: 'flex', gap: '15px', marginTop: '15px', padding: '15px', backgroundColor: '#111827', borderRadius: '6px' },
    statBox: { flex: 1, textAlign: 'center', padding: '10px', borderRadius: '4px' },
    statAdded: { backgroundColor: 'rgba(22,163,74,0.2)', color: '#4ade80' },
    statUpdated: { backgroundColor: 'rgba(99,102,241,0.2)', color: '#818cf8' },
    statSkipped: { backgroundColor: 'rgba(156,163,175,0.2)', color: '#9ca3af' },
  };

  const paginationStyles = {
    container: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', padding: '10px 0' },
    info: { color: '#9ca3af', fontSize: '14px' },
    controls: { display: 'flex', gap: '8px', alignItems: 'center' },
    button: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 12px', backgroundColor: '#1f2937', border: '1px solid #374151', color: '#f9fafb', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', minWidth: '40px' },
    activeButton: { backgroundColor: '#6366f1', borderColor: '#6366f1' },
    disabledButton: { opacity: 0.5, cursor: 'not-allowed' },
    pageNumbers: { display: 'flex', gap: '4px' },
  };

  const styles = {
    container: { padding: '40px 60px', backgroundColor: '#111827', minHeight: '100vh', color: '#f9fafb', fontFamily: "'Inter', -apple-system, sans-serif" },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' },
    headerTitle: { display: 'flex', alignItems: 'center', gap: '15px' },
    title: { fontSize: '28px', fontWeight: '700', margin: 0, background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
    refreshButton: { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '8px', borderRadius: '6px', display: 'flex', alignItems: 'center' },
    buttonGroup: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
    button: { display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', borderRadius: '8px', backgroundColor: '#1f2937', color: '#f9fafb', border: '1px solid #374151', cursor: 'pointer', fontSize: '14px', fontWeight: '500' },
    pendingBillButton: { backgroundColor: '#7c3aed', color: '#fff', border: 'none' },
    primaryButton: { backgroundColor: '#6366f1', color: '#fff', border: 'none' },
    saveButton: { backgroundColor: '#16a34a', color: '#fff', border: 'none' },
    searchContainer: { marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center' },
    searchWrapper: { position: 'relative', flex: 1, maxWidth: '400px' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' },
    searchInput: { width: '100%', padding: '10px 10px 10px 40px', backgroundColor: '#1f2937', border: '1px solid #374151', color: '#fff', borderRadius: '8px', fontSize: '14px' },
    tableContainer: { overflowX: 'auto', borderRadius: '10px', border: '1px solid #374151' },
    table: { width: '100%', borderCollapse: 'collapse', backgroundColor: '#1f2937', minWidth: '1100px' },
    th: { backgroundColor: '#374151', padding: '12px 14px', textAlign: 'left', color: '#f3f4f6', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
    td: { padding: '13px 14px', borderTop: '1px solid #374151', color: '#f9fafb', fontSize: '14px' },
    actionButtons: { display: 'flex', gap: '8px' },
    editButton: { background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: '4px', color: '#6366f1' },
    deleteButton: { background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: '4px', color: '#ef4444' },
    message: { padding: '12px 20px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', fontWeight: '500', whiteSpace: 'pre-line' },
    successMessage: { backgroundColor: 'rgba(22,163,74,0.2)', color: '#4ade80', border: '1px solid #16a34a' },
    errorMessage: { backgroundColor: 'rgba(220,38,38,0.2)', color: '#f87171', border: '1px solid #dc2626' },
    infoMessage: { backgroundColor: 'rgba(59,130,246,0.2)', color: '#60a5fa', border: '1px solid #3b82f6' },
    loadingOverlay: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px', color: '#9ca3af' },
    emptyState: { textAlign: 'center', padding: '60px', color: '#9ca3af', fontStyle: 'italic' },
    badge: { display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '500', marginLeft: '8px' },
    newBadge: { backgroundColor: '#6366f1', color: '#fff' },
  };

  // ================= RENDER =================
  return (
    <div style={styles.container}>

      {/* Edit Item Modal */}
      {showEditModal && editingItem && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.content}>
            <div style={modalStyles.modalHeader}>
              <h2 style={modalStyles.modalTitle}>
                <Edit size={18} style={{ marginRight: '8px', display: 'inline', verticalAlign: 'middle' }} />
                {editingItem.isNew ? '✨ Add New Product' : '✏️ Edit Product'}
              </h2>
              <button style={modalStyles.closeButton} onClick={() => { setShowEditModal(false); setEditingItem(null); }}>
                <X size={20} />
              </button>
            </div>

            {/* Item Type */}
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>Item Type</label>
              <select
                style={modalStyles.input}
                value={editingItem.itemType || "gold"}
                onChange={(e) => handleEditChange("itemType", e.target.value)}
              >
                <option value="gold">🥇 Gold</option>
                <option value="silver">🥈 Silver</option>
              </select>
            </div>

            {/* Item Name & Code */}
            <div style={modalStyles.formRow}>
              <div>
                <label style={modalStyles.label}>Item Name *</label>
                <input
                  style={modalStyles.input}
                  value={editingItem.name || ""}
                  onChange={(e) => handleEditChange("name", e.target.value)}
                  placeholder="e.g. BANGLES 3"
                />
              </div>
              <div>
                <label style={modalStyles.label}>Code</label>
                <input
                  style={modalStyles.input}
                  value={editingItem.code || ""}
                  onChange={(e) => handleEditChange("code", e.target.value)}
                  placeholder="Optional code"
                />
              </div>
            </div>

            {/* Gold & Silver Rate */}
            <div style={modalStyles.formRow}>
              <div>
                <label style={modalStyles.label}>Gold Rate (₹/g)</label>
                <input
                  style={modalStyles.input}
                  type="number"
                  min="0"
                  step="1"
                  value={editingItem.goldRate ?? ""}
                  onChange={(e) => handleEditChange("goldRate", e.target.value)}
                  placeholder="e.g. 7500"
                />
              </div>
              <div>
                <label style={modalStyles.label}>Silver Rate (₹/g)</label>
                <input
                  style={modalStyles.input}
                  type="number"
                  min="0"
                  step="1"
                  value={editingItem.silverRate ?? ""}
                  onChange={(e) => handleEditChange("silverRate", e.target.value)}
                  placeholder="e.g. 95"
                />
              </div>
            </div>

            {/* Weights */}
            <div style={modalStyles.formRow}>
              <div>
                <label style={modalStyles.label}>Gross Weight (g)</label>
                <input
                  style={modalStyles.input}
                  type="number"
                  min="0"
                  step="0.001"
                  value={editingItem.grossWeight ?? ""}
                  onChange={(e) => handleEditChange("grossWeight", e.target.value)}
                  placeholder="0.000"
                />
              </div>
              <div>
                <label style={modalStyles.label}>Stone / Wax Weight (g)</label>
                <input
                  style={modalStyles.input}
                  type="number"
                  min="0"
                  step="0.001"
                  value={editingItem.stoneWaxWeight ?? ""}
                  onChange={(e) => handleEditChange("stoneWaxWeight", e.target.value)}
                  placeholder="0.000"
                />
              </div>
            </div>

            <div style={modalStyles.formRow}>
              <div>
                <label style={modalStyles.label}>Net Weight (g) — Auto</label>
                <div style={modalStyles.readOnlyField}>
                  {(parseFloat(editingItem.netWeight) || 0).toFixed(3)} g
                </div>
              </div>
              <div>
                <label style={modalStyles.label}>Purity (%) — 60 to 99.9</label>
                <input
                  style={modalStyles.input}
                  type="number"
                  min="60"
                  max="99.9"
                  step="0.1"
                  value={editingItem.purity ?? 91}
                  onChange={(e) => handleEditChange("purity", e.target.value)}
                  placeholder="91"
                />
              </div>
            </div>

            {/* Gross Amount (auto) */}
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>Gross Amount (₹) — Auto-calculated</label>
              <div style={{ ...modalStyles.readOnlyField, fontSize: '18px', color: '#34d399' }}>
                ₹{(parseFloat(editingItem.grossAmount) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </div>
            </div>

            {/* Quantity */}
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>Quantity (stock)</label>
              <input
                style={modalStyles.input}
                type="number"
                min="0"
                step="1"
                value={editingItem.quantity ?? 1}
                onChange={(e) => handleEditChange("quantity", e.target.value)}
                placeholder="1"
              />
            </div>

            <div style={modalStyles.modalFooter}>
              <button style={styles.button} onClick={() => { setShowEditModal(false); setEditingItem(null); }}>
                Cancel
              </button>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={handleEditSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : (editingItem.isNew ? '✅ Create Product' : '💾 Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Items Modal */}
      {showImportModal && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.largeContent}>
            <div style={modalStyles.modalHeader}>
              <h2 style={modalStyles.modalTitle}>
                <Upload size={20} style={{ marginRight: '8px', display: 'inline' }} />
                Import Products ({importedItems.length} found)
              </h2>
              <button style={modalStyles.closeButton} onClick={() => { setShowImportModal(false); setImportedItems([]); }}>
                <X size={20} />
              </button>
            </div>

            {importStats.added > 0 || importStats.skipped > 0 ? (
              <div style={modalStyles.statsContainer}>
                <div style={{ ...modalStyles.statBox, ...modalStyles.statAdded }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{importStats.added}</div>
                  <div style={{ fontSize: '12px' }}>Added</div>
                </div>
                <div style={{ ...modalStyles.statBox, ...modalStyles.statSkipped }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{importStats.skipped}</div>
                  <div style={{ fontSize: '12px' }}>Skipped</div>
                </div>
              </div>
            ) : (
              <>
                <p style={{ color: '#9ca3af', marginBottom: '15px', fontSize: '13px' }}>
                  Select the items to import. Required column: <strong>Item Name</strong>. Optional: Code, Item Type, Gold Rate, Silver Rate, Gross Weight, Stone/Wax Weight, Purity, Quantity.
                </p>
                <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                  <table style={modalStyles.importTable}>
                    <thead>
                      <tr>
                        <th style={modalStyles.importTh}>
                          <input type="checkbox" checked={importedItems.every(i => i.selected)} onChange={toggleAllImportItems} style={modalStyles.checkbox} />
                        </th>
                        <th style={modalStyles.importTh}>Item Name</th>
                        <th style={modalStyles.importTh}>Code</th>
                        <th style={modalStyles.importTh}>Type</th>
                        <th style={modalStyles.importTh}>Gold Rate</th>
                        <th style={modalStyles.importTh}>Gross Wt</th>
                        <th style={modalStyles.importTh}>Stone/Wax</th>
                        <th style={modalStyles.importTh}>Net Wt</th>
                        <th style={modalStyles.importTh}>Purity%</th>
                        <th style={modalStyles.importTh}>Gross Amt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importedItems.map((item, index) => (
                        <tr key={item.id}>
                          <td style={modalStyles.importTd}>
                            <input type="checkbox" checked={item.selected} onChange={() => toggleImportItem(index)} style={modalStyles.checkbox} />
                          </td>
                          <td style={modalStyles.importTd}>{item.name || '-'}</td>
                          <td style={modalStyles.importTd}>{item.code || '-'}</td>
                          <td style={modalStyles.importTd}>{item.itemType || 'gold'}</td>
                          <td style={modalStyles.importTd}>₹{item.goldRate || 0}</td>
                          <td style={modalStyles.importTd}>{item.grossWeight || 0}g</td>
                          <td style={modalStyles.importTd}>{item.stoneWaxWeight || 0}g</td>
                          <td style={modalStyles.importTd}>{(item.netWeight || 0).toFixed(3)}g</td>
                          <td style={modalStyles.importTd}>{item.purity || 91}%</td>
                          <td style={modalStyles.importTd}>₹{(item.grossAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={modalStyles.modalFooter}>
                  <button style={styles.button} onClick={() => { setShowImportModal(false); setImportedItems([]); }}>Cancel</button>
                  <button style={{ ...styles.button, ...styles.primaryButton }} onClick={processImportedItems} disabled={processingImport}>
                    {processingImport ? 'Processing...' : 'Import Selected'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}



      {/* Main Content */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          <h1 style={styles.title}>🏅 Product Master</h1>
          <button style={styles.refreshButton} onClick={handleRefresh} title="Refresh">
            <RefreshCw size={18} />
          </button>
        </div>

        <div style={styles.buttonGroup}>
          <button style={styles.button} onClick={handleExport}>
            <Download size={16} /> Export
          </button>

          <label style={styles.button}>
            <Upload size={16} /> Import
            <input type="file" hidden onChange={handleImport} accept=".xlsx,.xls,.csv" />
          </label>



          <button style={{ ...styles.button, ...styles.primaryButton }} onClick={handleAddNewItem}>
            <Plus size={16} /> Add New Product
          </button>

          <button style={{ ...styles.button, ...styles.saveButton }} onClick={handleRefresh} disabled={saving}>
            <Save size={16} /> {saving ? "Saving..." : "Refresh"}
          </button>
        </div>
      </div>

      {message.text && (
        <div style={{ ...styles.message, ...(message.type === "success" ? styles.successMessage : message.type === "error" ? styles.errorMessage : styles.infoMessage) }}>
          {message.text.split('\n').map((line, i) => <div key={i}>{line}</div>)}
        </div>
      )}

      <div style={styles.searchContainer}>
        <div style={styles.searchWrapper}>
          <Search size={16} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by name, code, item type..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        <span style={{ color: "#9ca3af", fontSize: "13px" }}>
          {filteredItems.length} item(s)
        </span>
      </div>

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={styles.loadingOverlay}>Loading products...</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}><Hash size={12} style={{ display: 'inline', marginRight: '4px' }} />ID</th>
                <th style={styles.th}>Item Name</th>
                <th style={styles.th}>Code</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Gold Rate (₹/g)</th>
                <th style={styles.th}>Gross Wt (g)</th>
                <th style={styles.th}>Stone/Wax (g)</th>
                <th style={styles.th}>Net Wt (g)</th>
                <th style={styles.th}>Purity (%)</th>
                <th style={styles.th}>Gross Amount (₹)</th>
                <th style={styles.th}>Qty</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="12" style={styles.emptyState}>
                    {search ? "No products match your search" : "No products found. Click 'Add New Product' to get started."}
                  </td>
                </tr>
              ) : (
                currentItems.map((item, index) => (
                  <tr key={item.id} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#263348'} onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                    <td style={styles.td}>
                      <span style={{ fontFamily: 'monospace', color: '#9ca3af' }}>#{item.displayId ?? index + 1}</span>
                      {item.isNew && <span style={{ ...styles.badge, ...styles.newBadge }}>NEW</span>}
                    </td>
                    <td style={{ ...styles.td, fontWeight: '600' }}>{item.name || '-'}</td>
                    <td style={styles.td}>{item.code || '-'}</td>
                    <td style={styles.td}>
                      <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', backgroundColor: item.itemType === 'gold' ? '#fbbf24' : '#d1d5db', color: '#1f2937' }}>
                        {item.itemType === 'gold' ? '🥇 Gold' : '🥈 Silver'}
                      </span>
                    </td>
                    <td style={styles.td}>₹{(item.goldRate || 0).toLocaleString('en-IN')}</td>
                    <td style={styles.td}>{(item.grossWeight || 0).toFixed(3)}</td>
                    <td style={styles.td}>{(item.stoneWaxWeight || 0).toFixed(3)}</td>
                    <td style={{ ...styles.td, color: '#60a5fa', fontWeight: '600' }}>{(item.netWeight || 0).toFixed(3)}</td>
                    <td style={styles.td}>{(item.purity || 91)}%</td>
                    <td style={{ ...styles.td, color: '#34d399', fontWeight: '700' }}>
                      ₹{(item.grossAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </td>
                    <td style={styles.td}>{item.quantity || 1}</td>
                    <td style={styles.td}>
                      <div style={styles.actionButtons}>
                        <button style={styles.editButton} onClick={() => handleEditClick(item)} title="Edit">
                          <Edit size={16} />
                        </button>
                        <button style={styles.deleteButton} onClick={() => handleDelete(item.id)} title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <div style={paginationStyles.container}>
          <div style={paginationStyles.info}>
            Page {currentPage} of {totalPages} | {totalItems} total products
          </div>
          <div style={paginationStyles.controls}>
            <button onClick={goToPreviousPage} disabled={currentPage === 1} style={{ ...paginationStyles.button, ...(currentPage === 1 ? paginationStyles.disabledButton : {}) }}>
              <ChevronLeft size={16} />
            </button>
            <div style={paginationStyles.pageNumbers}>
              {[...Array(totalPages)].map((_, index) => {
                const pageNumber = index + 1;
                if (pageNumber === 1 || pageNumber === totalPages || (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)) {
                  return (
                    <button key={pageNumber} onClick={() => paginate(pageNumber)} style={{ ...paginationStyles.button, ...(currentPage === pageNumber ? paginationStyles.activeButton : {}) }}>
                      {pageNumber}
                    </button>
                  );
                } else if (pageNumber === currentPage - 3 || pageNumber === currentPage + 3) {
                  return <span key={pageNumber} style={{ color: '#9ca3af', padding: '0 4px' }}>...</span>;
                }
                return null;
              })}
            </div>
            <button onClick={goToNextPage} disabled={currentPage === totalPages} style={{ ...paginationStyles.button, ...(currentPage === totalPages ? paginationStyles.disabledButton : {}) }}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}